import { AfterViewInit, Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Observable, tap, map, forkJoin, of } from "rxjs";
import { CoreGridComponent } from "src/app/shared/grid/core-grid/core-grid.component";
import {
    ColumnConfig,
    ChildColumnConfig,
} from "src/app/shared/grid/core-grid/columnConfig.model";
import { OverviewConfig } from "src/app/shared/grid/core-grid/overviewConfig.model";
import { SensorModel } from "src/app/models/sensor.model";
import { SensorService } from "src/app/services/sensor.service";
import { CommonService } from "src/app/services/common.service";
import { ToastService } from "src/app/services/toast.service";
import { AuthService } from "src/app/services/auth.service";
import { OperatorTypeEnum } from "src/app/enums/operatorType.enum";
import nameOf from "src/app/shared/form/nameOf.helper";
import { Router } from "@angular/router";
import {
    INodeAssignmentFormDialogData,
    NodeAssignmentDialogComponent,
} from "src/app/modals/node-assignment-dialog/node-assignment-dialog.component";
import { DateFormatPipe } from "src/app/shared/pipes/date-format.pipe";
import { BatteryService } from "src/app/services/battery.service";
import { SensorTypeEnum } from "src/app/enums/sensorType.enum";
import { TrackingTypeEnum } from "src/app/enums/trackingType.enum";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import { FlyoutContainerComponent } from "src/app/shared/flyout/flyout-container/flyout-container.component";
import { DeviceInfoComponent } from "src/app/modals/device-info/device-info.component";
import { DateFormatService } from "src/app/services/dateFormat.service";
import { getFilteredDevicesRouteConfig } from "src/app/shared/constants/devicesRouteConfig.constants";
import { GridViewService } from "src/app/services/grid-view.service";
import { GridSettingsService } from "src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service";
import { OrganizationLevelService } from "src/app/services/organizationLevel.service";
import { Lookup } from "src/app/models/lookup.model";
import { TranslateService } from "src/app/services/translate.service";
import { LocalStorageConstants } from "src/app/shared/constants/localStorage.constants";
import { RoutePaths } from "src/app/shared/constants/routePaths.constants";
import { StatusLabels } from "src/app/shared/constants/status.constants";

@Component({
  standalone: false,
    selector: "app-devices-table",
    templateUrl: "./../../shared/grid/core-grid/core-grid.component.html",
    styleUrls: [
        "./../../shared/grid/core-grid/core-grid.component.scss",
        "./devices-table.component.scss",
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    providers: [DateFormatPipe],
})
export class DevicesTableComponent
    extends CoreGridComponent<SensorModel>
    implements OnInit, AfterViewInit
{
    @ViewChild(FlyoutContainerComponent) flyout!: FlyoutContainerComponent;

    organizationLevels: Lookup[] = [];
    calibrationCount: number = 0;
    replacementCount: number = 0;
    apiDevicesCount: number = 0;
    externalDevicesCount: number = 0;
    minimaCount: number = 0;
    maximaCount: number = 0;
    lowBatteryDevicesCount: number = 0;

    constructor(
        private sensorService: SensorService,
        toastService: ToastService,
        dialog: MatDialog,
        commonService: CommonService,
        authService: AuthService,
        private router: Router,
        private dateFormatPipe: DateFormatPipe,
        private batteryService: BatteryService,
        private dateFormatService: DateFormatService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        private translateService: TranslateService,
        private organizationLevelService: OrganizationLevelService,
    ) {
        super(
            toastService,
            dialog,
            commonService,
            authService,
            gridViewService,
            gridSettingsService,
        );
        this.gridService = sensorService;
        this.deleteDisabled = true;
        this.editDisabled = true;
        this.addDisabled = true;
        this.overviewWidth = "25%";
    }

    protected get objectId(): string {
        return RolePermissionObjects.Sensor;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    get hasOrgFilter(): boolean {
        return true;
    }

    ngOnInit(): void {
        super.ngOnInit();
        this.organizationLevelService
            .getAll()
            .subscribe((orgs) => (this.organizationLevels = orgs));
    }

    ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.loadHeaderData();
    }

    override initializeColumns(): void {
        this.columnConfigs = [
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.name),
                displayName: "Device Name",
                cssClass: (entity) =>
                    `device-status ${entity.status ? "activeS" : ""}`,
                cellTemplate: () => "",
                // The grid uses the first column-level onClick as the row-click action, so opening the
                // device lives on this column rather than on its name child.
                onClick: (entity) => this.viewDevice(entity.id),
                children: () => [
                    new ChildColumnConfig<SensorModel>({
                        cellTemplate: (entity) => entity.name || "-",
                        cssClass: () => "entity-name",
                    }),
                    new ChildColumnConfig<SensorModel>({
                        imgTooltips: () => ["Video"],
                        cssClass: () => "device-icon",
                        imgSrcs: (entity) =>
                            entity.videoCapability
                                ? ["./assets/images/Media.svg"]
                                : [],
                        cellTemplate: () => "",
                    }),
                    new ChildColumnConfig<SensorModel>({
                        imgTooltips: () => ["GPS"],
                        cssClass: () => "device-icon",
                        imgSrcs: (entity) =>
                            entity.gpsCapability
                                ? ["./assets/images/GPS.svg"]
                                : [],
                        cellTemplate: () => "",
                    }),
                    new ChildColumnConfig<SensorModel>({
                        cssClass: () => "device-icon",
                        imgSrcs: (entity) =>
                            this.batteryService.getBatteryImgSrcs(entity),
                        imgTooltips: (entity) =>
                            this.batteryService.getBatteryTooltips(entity),
                        cellTemplate: () => "",
                    }),
                ],
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.id),
                displayName: "Device ID",
                cellTemplate: (entity) => entity.id || "-",
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.organizationLevelName),
                displayName: "Site/Dept",
                cellTemplate: (entity) => entity.organizationLevelName || "-",
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.endPointName),
                displayName: "Node Name",
                isSortable: false,
                tooltip: (entity) =>
                    this.isEndPointAssigned(entity)
                        ? ""
                        : this.translateService.getDynamicTranslation("Assign"),
                cssClass: (entity) =>
                    this.isEndPointAssigned(entity) ? "" : "btn-asg",
                cellTemplate: (entity) =>
                    this.isEndPointAssigned(entity)
                        ? entity.endPointName || "-"
                        : this.translateService.getDynamicTranslation(
                              "Assign EP",
                          ),
                iconCssClass: (entity) =>
                    this.isEndPointAssigned(entity)
                        ? `${entity.endPointIcon || ""} float-left-icon`
                        : "",
                onClick: (entity) =>
                    this.isEndPointAssigned(entity)
                        ? this.viewDevice(entity.id)
                        : this.openSensorAssignmentDialog(entity, true),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.installationDate),
                displayName: "Installation Date",
                cellTemplate: (entity) =>
                    this.dateFormatPipe.transform(entity.installationDate),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.deactivationDate),
                displayName: "Renewal Date",
                cellTemplate: (entity) =>
                    this.dateFormatPipe.transform(entity.deactivationDate),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.configurationCapability),
                displayName: "Configuration Capability",
                cellTemplate: (e) =>
                    this.translateService.getDynamicTranslation(
                        e.configurationCapability ? "Yes" : "No",
                    ),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.organizationLevelId),
                displayName: "Visibility",
                cellTemplate: (e) =>
                    this.organizationLevels?.find(
                        (org) => org.id === e.organizationLevelId,
                    )?.name || "-",
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.sensorType),
                displayName: "Sensor Type",
                cellTemplate: (e) =>
                    SensorTypeEnum[e.sensorType]
                        ? this.translateService.getDynamicTranslation(
                              SensorTypeEnum[e.sensorType],
                          )
                        : "-",
                enumFilterMap: this.enumToValueNameArray(SensorTypeEnum),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.trackingType),
                displayName: "Tracking Type",
                cellTemplate: (e) =>
                    TrackingTypeEnum[e.trackingType]
                        ? this.translateService.getDynamicTranslation(
                              TrackingTypeEnum[e.trackingType],
                          )
                        : "-",
                enumFilterMap: this.enumToValueNameArray(TrackingTypeEnum),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.batteryCharging),
                displayName: "Battery Charging",
                cellTemplate: (e) =>
                    this.translateService.getDynamicTranslation(
                        e.batteryCharging ? "Yes" : "No",
                    ),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.batteryPercentage),
                displayName: "Battery Percentage",
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.lastUpdated),
                displayName: "Last Updated",
                cellTemplate: (entity) =>
                    this.dateFormatPipe.transform(entity.lastUpdated),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.status),
                displayName: "Status",
                cellTemplate: (e) =>
                    this.translateService.getDynamicTranslation(
                        e.status ? StatusLabels.active : StatusLabels.inactive,
                    ),
            }),
            new ColumnConfig<SensorModel>({
                name: nameOf<SensorModel>((x) => x.desiredOrientation),
                displayName: "Desired Orientation",
            }),
            //TODO : Missing design for flyout icon (info icon)
            { name: "action", displayName: "Action" },
        ];
        this.additionalActions = [
            {
                name: "Info",
                icon: "fa fa-info-circle",
                action: (entity: SensorModel) =>
                    this.openDeviceInfoFlyout(entity),
            },
        ];
    }

    override objectFilterMap(propertyName: string): any | undefined {
        switch (propertyName) {
            case nameOf<SensorModel>((x) => x.organizationLevelId):
                return this.organizationLevelService;
            default:
                return undefined;
        }
    }

    override initializeOverviewConfigs(): void {
        this.overviewConfigs = [
            new OverviewConfig({
                title: "Devices with Low Battery",
                value: this.lowBatteryDevicesCount,
                onClick: () => this.filterDevicesWithLowBattery(),
            }),
            new OverviewConfig({
                title: "Calibration Count",
                value: this.calibrationCount,
                onClick: () => this.filterByCalibration(),
            }),
            new OverviewConfig({
                title: "Replacement Count",
                value: this.replacementCount,
                onClick: () => this.filterByReplacement(),
            }),
            new OverviewConfig({
                title: "API Devices",
                value: this.apiDevicesCount,
                onClick: () => this.filterBySensorType(SensorTypeEnum.API),
            }),
            new OverviewConfig({
                title: "External Devices",
                value: this.externalDevicesCount,
                onClick: () => this.filterBySensorType(SensorTypeEnum.EXT),
            }),
            new OverviewConfig({
                title: "Minima Devices",
                value: this.minimaCount,
                onClick: () => this.filterBySensorType(SensorTypeEnum.MINIMA),
            }),
            new OverviewConfig({
                title: "Maxima Devices",
                value: this.maximaCount,
                onClick: () => this.filterBySensorType(SensorTypeEnum.MAXIMA),
            }),
        ];
    }

    override initializeTabs(): { routerLink: string; title: string }[] {
        const currentUser = this.authService.getCurrentUser();
        return getFilteredDevicesRouteConfig(
            currentUser.organizationLevelPermissions,
        );
    }

    override initializeProperties(): void {
        this.addEntityTitle = "Device";
        this.searchPlaceholder = "Search Devices";
    }

    override getOverviewData(): Observable<void> {
        return forkJoin({
            lowBatteryCount: this.sensorService
                .getLowBatterySensorCount(30)
                .pipe(
                    tap((data: number) => {
                        this.lowBatteryDevicesCount = data;
                    }),
                ),
            sensorCounts: this.getSensorCounts(),
        }).pipe(map(() => undefined));
    }

    private isEndPointAssigned(entity: SensorModel): boolean {
        return !!entity.endPointId;
    }

    private getSensorCounts(): Observable<void> {
        return this.sensorService.getDeviceSummaryData().pipe(
            tap((data) => {
                this.calibrationCount = data.calibrationCount;
                this.replacementCount = data.replacementCount;
                this.apiDevicesCount = 0;
                this.externalDevicesCount = 0;
                this.minimaCount = 0;
                this.maximaCount = 0;

                const sensorTypeCounts = {
                    [SensorTypeEnum.API]: "apiDevicesCount",
                    [SensorTypeEnum.EXT]: "externalDevicesCount",
                    [SensorTypeEnum.MINIMA]: "minimaCount",
                    [SensorTypeEnum.MAXIMA]: "maximaCount",
                };

                for (const key of Object.keys(data.sensorTypes)) {
                    const enumKey = this.getEnumKeyFromCaseInsensitiveKey(
                        SensorTypeEnum,
                        key,
                    );
                    if (
                        enumKey !== undefined &&
                        sensorTypeCounts[SensorTypeEnum[enumKey]]
                    ) {
                        this[sensorTypeCounts[SensorTypeEnum[enumKey]]] +=
                            data.sensorTypes[key];
                    }
                }
            }),
            map(() => undefined),
        );
    }

    getEnumKeyFromCaseInsensitiveKey(
        enumType: any,
        lowercaseKey: string,
    ): string | undefined {
        return Object.keys(enumType).find(
            (enumKey) => enumKey.toLowerCase() === lowercaseKey.toLowerCase(),
        );
    }

    protected filterByCalibration(): void {
        const utcNow = this.dateFormatService.convertDateToTimestamp(
            new Date(),
        );

        this.additionalFilters[`OperatorTypeEnum.LessThan_nextCalibration`] =
            JSON.stringify({
                Condition: OperatorTypeEnum.LessThan.toString(),
                Field: "nextCalibration",
                Value: utcNow.toString(),
            });
    }

    protected filterByReplacement(): void {
        this.additionalFilters[`OperatorTypeEnum.Equal_replacementRequested`] =
            JSON.stringify({
                Condition: OperatorTypeEnum.Equal.toString(),
                Field: "replacementRequested",
                Value: "true",
            });
    }

    protected filterBySensorType(sensorType: SensorTypeEnum): void {
        this.additionalFilters[`OperatorTypeEnum.Equal_sensorType`] =
            JSON.stringify({
                Condition: OperatorTypeEnum.Equal.toString(),
                Field: "sensorType",
                Value: sensorType.toString(),
            });
    }

    //TODO: Transfer to url without localstorage
    viewDevice(sensorId: string): void {
        const encSensorId = window.btoa(sensorId);
        localStorage.setItem(LocalStorageConstants.deviceName, encSensorId);
        this.router.navigate([RoutePaths.deviceDetail]);
    }

    filterDevicesWithLowBattery(): void {
        this.additionalFilters["OperatorTypeEnum"] = JSON.stringify({
            Condition: OperatorTypeEnum.LessThan.toString(),
            Field: "batterypercentage",
            Value: "30",
        });
    }

    openSensorAssignmentDialog(sensor: SensorModel, assignNew: boolean): void {
        const nodeAssignmentModel: INodeAssignmentFormDialogData = {
            id: '',
            sensorId: sensor.id,
            sensorName: sensor.name,
            organizationLevelId: sensor.organizationLevelId,
            isNodeAssignment: false,
        };

        this.openDialog(
            NodeAssignmentDialogComponent,
            nodeAssignmentModel,
            false,
            "550px",
        );
    }

    override openEntityDialog(entityId?: string): void {
        return;
    }

    override openEntityDeleteDialog(entity: SensorModel): void {
        return;
    }

    openDeviceInfoFlyout(entity: SensorModel): void {
        this.openFlyoutInternal(DeviceInfoComponent, entity, "510px");
    }
}
